import {
  Check,
  ChevronDown,
  ChevronUp,
  Search,
  User,
  UserPlus,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

export default function AssignSalesDropdown({
  sales = [],
  selectedSalesId,
  onAssign,
  disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAssigning, setIsAssigning] = useState(false)
  const assigningRef = useRef(false)

  const filteredSales = useMemo(() => {
    if (!searchQuery.trim()) return sales
    const q = searchQuery.toLowerCase().trim()
    return sales.filter((person) => person.name?.toLowerCase().includes(q))
  }, [sales, searchQuery])

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setSearchQuery('')
    }
    setOpen(isOpen)
  }

  const handleAssign = async (person) => {
    if (assigningRef.current) return

    assigningRef.current = true
    setIsAssigning(true)
    setOpen(false)

    try {
      await onAssign?.(person)
    } catch (error) {
      console.error(error)
    } finally {
      assigningRef.current = false
      setIsAssigning(false)
    }
  }

  const isDisabled = disabled || isAssigning

  return (
    <DropdownMenu
      open={isDisabled ? false : open}
      onOpenChange={handleOpenChange}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="navy"
          disabled={isDisabled}
          className="h-8 gap-1 rounded-full px-2.5 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
        >
          <UserPlus
            className={`size-3.5 ${isAssigning ? 'animate-spin' : ''}`}
          />
          {isAssigning ? 'Assigning...' : 'Assign'}
          {!isAssigning &&
            (open ? (
              <ChevronUp className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            ))}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={6}
        collisionPadding={16}
        className="w-64 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200/90 bg-white p-2 shadow-xl"
      >
        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 mb-1">
          <span className="text-xs font-bold text-slate-800">
            Assign to salesperson
          </span>
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {filteredSales.length}
          </span>
        </div>

        {sales.length > 4 && (
          <div className="px-1 pb-1.5 pt-0.5">
            <div className="relative flex items-center">
              <Search className="absolute left-2.5 size-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search salesperson..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/80 py-1.5 pl-8 pr-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
        )}

        <div className="max-h-56 overflow-y-auto overscroll-contain pr-0.5 space-y-0.5 [scrollbar-width:thin]">
          {filteredSales.length > 0 ? (
            filteredSales.map((person) => (
              <DropdownMenuItem
                key={person._id}
                disabled={isAssigning}
                onSelect={() => handleAssign(person)}
                className="flex items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-700">
                    {person.name?.[0]?.toUpperCase() || (
                      <User className="size-3" />
                    )}
                  </div>
                  <span className="truncate">{person.name}</span>
                </div>

                {selectedSalesId === person._id && (
                  <Check className="h-4 w-4 shrink-0 text-indigo-600 font-bold" />
                )}
              </DropdownMenuItem>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-xs text-slate-400 font-normal">
              {searchQuery
                ? 'No matching salespersons'
                : 'No salespersons available'}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
